import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import {
  ImageUpload,
  ImageUploadProps as BaseImageUploadProps,
} from "@/components/inference-forms/common/Upload/ImageUpload";
import { FormHelperText } from "@/components/ui-v2";
import { findPresetByDimensions } from "@/components/ui-v2/AspectRatio/utils";
import { LabelRow } from "@/components/ui-v2/LabelRow/LabelRow";

import { FIELD_NAMES } from "../FieldDefs/constants";
import { FileReferenceType } from "../types";
import { UploadRoot } from "./UploadRoot";

export type ImageUploadFieldProps = Omit<
  BaseImageUploadProps,
  "value" | "onChange" | "currentPreset" | "onPresetApplied"
> & {
  label: string;
  tooltip?: string;
};

export const ImageUploadField = (
  props: {
    value: FileReferenceType[];
    onChange: (v: FileReferenceType[]) => void;
    error?: { message?: string };
    disabled?: boolean;
  } & ImageUploadFieldProps,
) => {
  const {
    label,
    tooltip,
    disabled,
    value,
    onChange,
    error,
    aspectRatioPresets,
    ...rest
  } = props;

  const errorMessage = error?.message;

  const watchedDimensions = useWatch({ name: FIELD_NAMES.dimensions });
  const currentPreset = useMemo(
    () =>
      aspectRatioPresets && watchedDimensions
        ? findPresetByDimensions(aspectRatioPresets, watchedDimensions)
        : undefined,
    [aspectRatioPresets, watchedDimensions],
  );

  return (
    <UploadRoot>
      <LabelRow
        disabled={disabled}
        label={label}
        useMuiLabel
        useHtmlLabel
        tooltip={tooltip}
      />
      {!disabled && (
        <>
          <ImageUpload
            {...rest}
            value={value}
            onChange={onChange}
            aspectRatioPresets={aspectRatioPresets}
            currentPreset={currentPreset}
          />
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
        </>
      )}
    </UploadRoot>
  );
};
